import { useOrg } from '@/contexts/OrgContext';
import { useAuth } from '@/contexts/AuthContext';

interface AdminOrgResult {
  orgId: string | null;
  org: ReturnType<typeof useOrg>['org'] | null;
  loading: boolean;
  canWrite: boolean;
  /** True when a platform admin is logged in but hasn't selected a tenant yet. */
  noOrgSelected: boolean;
}

/**
 * Returns the active org for admin pages.
 *
 * - Tenant admin/owner → their membership org (from AuthContext), never the
 *   apex/preview "Polished Version" public fallback that OrgContext resolves.
 * - Platform admin impersonating a tenant → that tenant's org (canWrite: false)
 * - Platform admin, no tenant selected → { orgId: null, noOrgSelected: true }
 *
 * Anchoring tenant admins to their own membership org is what keeps storage
 * writes (e.g. the `<orgId>/company/logo.png` upload path) inside the folder
 * their org owns — otherwise storage RLS denies the upload because the path
 * points at the fallback org's folder.
 */
export function useAdminOrg(): AdminOrgResult {
  const { org, orgId, loading, canWrite, isImpersonating } = useOrg();
  const { isPlatformAdmin, organizationMembership, rolesLoading } = useAuth();

  // Platform admins only ever act on an explicitly impersonated tenant. Without
  // one selected they are read-only with no org — never the public fallback.
  if (isPlatformAdmin) {
    if (isImpersonating) {
      // Platform admins are read-only even while impersonating — the documented
      // contract is canWrite: false, never OrgContext's value.
      return { org, orgId, loading, canWrite: false, noOrgSelected: false };
    }
    return { org: null, orgId: null, loading: loading || rolesLoading, canWrite: false, noOrgSelected: true };
  }

  // Tenant admins/owners are anchored to their own membership org so admin
  // reads and writes never inherit the apex/preview fallback org.
  if (organizationMembership) {
    const membershipId = organizationMembership.organizationId;
    // Reuse OrgContext's fully-resolved org when it already matches (the owner
    // is on their own subdomain/custom domain); otherwise synthesise a minimal
    // org from the membership so slug-dependent admin pages still render.
    const resolvedOrg =
      org.id === membershipId
        ? org
        : { ...org, id: membershipId, name: organizationMembership.organizationName };
    return {
      org: resolvedOrg,
      orgId: membershipId,
      loading: loading || rolesLoading,
      canWrite: true,
      noOrgSelected: false,
    };
  }

  // Membership not resolved yet. While roles are still loading we don't know the
  // tenant's org, so we must NOT expose the apex/preview public fallback org that
  // OrgContext resolves — otherwise admin reads (e.g. the company_info row, whose
  // query is only gated on a truthy orgId) briefly hit the public fallback org's
  // data before the membership anchor lands (issue 6 regression).
  if (rolesLoading) {
    return { org: null, orgId: null, loading: true, canWrite: false, noOrgSelected: false };
  }

  // Roles resolved but no membership (e.g. a team member without an org row) —
  // preserve the previous OrgContext-backed behaviour.
  return { org, orgId, loading, canWrite, noOrgSelected: false };
}
